/**
 * 문항 구조 — 프론트 편집용 노드 트리 모델.
 *
 * ⚠️ 이건 저장 포맷이 아니다. 백엔드로 나가는 sub_data_contents 는
 * 지금 모양 그대로 유지한다(계약). 이 트리는 편집기 안에서만 살고,
 * 불러올 때 fromV1() 로 만들고 저장할 때 toV1() 로 되돌린다.
 *
 * 왜 트리인가: 기존 렌더러는
 *   `structure[i].element_contents[j].structure[k]`
 * 처럼 경로에 깊이를 문자열로 박아 3단계까지만 다룬다. 자기 자신을 부르지 않으므로
 * 재귀가 아니다. staging 실데이터 375건 중 206건(55%)이 이미 4단계를 넘고 7단계도 있다.
 *
 * 무손실 원칙: toV1 은 트리에서 v1 을 "새로 만들지" 않는다.
 * **원본(_src)을 복제한 뒤 편집기가 소유한 부분만 덮어쓴다.**
 * 우리가 모델링하지 않은 키(미지의 확장, 첨부 메타 등)는 그대로 살아남는다.
 */

/** 행(row) 객체에서 컬럼이 아니라 설정인 키들. */
const ROW_META_KEYS = [
    'dropdown', 'calculation', 'noteditables', 'placeholder', 'input_type',
    // required_cells 는 '열 이름 목록' 이지 열이 아니다. 빠뜨리면 table_header 가 없는 표에서
    // 가짜 열이 생기고, 있는 표에서는 "열에 없는 값이 남아 있다" 경고로 뜬다.
    'required_cells',
    'attachments', 'element_contents', 'result', 'text', 'value'
]

/** `구분_ea`, `직무_ca` 같은 번역 사이드카 키인지. */
export const isTranslationKey = (key) => /_(ea|ca|em|cm)$/.test(key)
const isMetaKey = (key) => ROW_META_KEYS.includes(key) || isTranslationKey(key)

export const LOCALE_SUFFIX = {ea: '_ea', ca: '_ca'}

/**
 * guide_text2 는 번역을 사이드카가 아니라 **배열 자리**에 둔다 — 렌더러(DFGuideText2.vue:74-79)가
 * locale 로 structure[0]/[1]/[2] 를 직접 읽는다. prod 39건 전부가 이 모양이고 structure_ea 를 가진
 * 행은 0건이다. 이 자리를 지우면 영·중 본문이 사라진다.
 */
export const GUIDE_SLOT = {ea: 1, ca: 2}

/**
 * guide_text2 의 표시 옵션은 sub_data_header 가 아니라 **sub_data_contents** 에 있다 —
 * DFGuideText2 가 거기서 읽는다. 같은 이름이어도 guide_text 는 header 쪽이라 섞으면 안 된다.
 * (toggle_title 은 본문과 같은 자리 배열이라 GUIDE_SLOT 을 함께 쓴다)
 */
export const GUIDE2_DISPLAY_KEYS = ['is_toggle', 'toggled', 'html', 'bordered', 'margin_top', 'margin_bottom']

/**
 * 계산식 한 줄을 본다. 실데이터 형태는 `(b1+c1+d1)/3` — 셀 참조·숫자·괄호·사칙연산.
 * 깨진 식은 저장은 되는데 응답자 화면에서 조용히 계산이 안 된다. v1 도 저장을 막았다.
 */
export const validateCalc = (formula) => {
    const f = String(formula == null ? '' : formula).trim()
    if (!f) return {ok: false, msg: '계산식이 비어 있습니다'}
    if (/[^A-Za-z0-9가-힣_+\-*/().\s]/.test(f)) return {ok: false, msg: '쓸 수 없는 문자가 있습니다 (숫자·셀 이름·+ - * / 괄호만)'}
    let depth = 0
    for (const ch of f) {
        if (ch === '(') depth++
        else if (ch === ')') { depth--; if (depth < 0) return {ok: false, msg: '괄호가 맞지 않습니다'} }
    }
    if (depth !== 0) return {ok: false, msg: '괄호가 맞지 않습니다'}
    if (/[+\-*/]{2,}/.test(f.replace(/\s/g, ''))) return {ok: false, msg: '연산자가 잇달아 있습니다'}
    if (/^[*/]/.test(f) || /[+\-*/]$/.test(f.trim())) return {ok: false, msg: '계산식이 연산자로 시작하거나 끝납니다'}
    if (!/[A-Za-z0-9가-힣_]/.test(f)) return {ok: false, msg: '셀 이름이나 숫자가 없습니다'}
    return {ok: true, msg: ''}
}

/** 트리 안의 계산식 칸을 훑어 문제만 모은다. 저장 전에 막기 위해. */
export const calcProblems = (root) => {
    const out = []
    walk(root, (n, d, path) => {
        if (n.type !== 'calc') return
        const v = validateCalc(n.formula)
        if (!v.ok) out.push({id: n.id, where: path.join(' › ') || '루트', msg: v.msg, formula: n.formula || ''})
    })
    return out
}

/** 필수 셀로 지정할 수 있는 칸 (v1 EditingDFTable 은 입력 폼·드롭다운 칸만 허용한다) */
export const REQUIRABLE_CELL = ['input_text', 'input_number', 'dropdown']

/**
 * 노드 유형의 성질. 컨테이너는 이 성질로 자식을 거른다 —
 * 컨테이너마다 허용 목록을 두면 유형 추가 때 전부 고쳐야 한다.
 *   fit  : inline = 좁은 칸에 들어감 / block = 자기 영역이 필요함
 *   kind : content = 사용자가 자유롭게 놓음 / structural = 부모가 만들어 줌
 */
export const NODE_META = {
    plain_text:   {fit: 'inline', kind: 'content',    ko: '텍스트'},
    input_text:   {fit: 'inline', kind: 'content',    ko: '단답 입력'},
    input_number: {fit: 'inline', kind: 'content',    ko: '숫자 입력'},
    dropdown:     {fit: 'inline', kind: 'content',    ko: '드롭다운'},
    calc:         {fit: 'inline', kind: 'content',    ko: '계산식'},
    file_URL2:    {fit: 'inline', kind: 'content',    ko: '파일·URL 첨부'},
    radio:        {fit: 'block',  kind: 'content',    ko: '단일 선택'},
    checkbox:     {fit: 'block',  kind: 'content',    ko: '복수 선택'},
    table:        {fit: 'block',  kind: 'content',    ko: '표 입력'},
    textbox:      {fit: 'block',  kind: 'content',    ko: '서술형'},
    guide_text:   {fit: 'block',  kind: 'content',    ko: '안내문'},
    guide_text2:  {fit: 'block',  kind: 'content',    ko: '안내문(2)'},
    row:          {fit: 'block',  kind: 'structural', ko: '행'},
    column:       {fit: 'block',  kind: 'structural', ko: '열'},
    cell:         {fit: 'inline', kind: 'structural', ko: '칸'}
}

export const nodeMeta = (type) => NODE_META[type] || {fit: 'block', kind: 'content', ko: type}

export const cellAccepts = (type) => {
    const m = nodeMeta(type)
    return m.fit === 'inline' && m.kind === 'content'
}

export const whyNotInCell = (type) => {
    const m = nodeMeta(type)
    if (m.kind === 'structural') return '부모가 만들어 주는 노드라 직접 넣을 수 없습니다'
    if (m.fit === 'block') return '칸 안에는 넣을 수 없습니다 (자기 영역이 필요한 유형)'
    return ''
}

/**
 * 선택지를 자식으로 갖는 유형.
 * 자식 유형은 일부러 제한하지 않는다 — 어떤 노드든 선택지가 될 수 있어야 한다.
 */
export const CHOICE_PARENTS = ['radio', 'checkbox', 'dropdown']

/**
 * v2 편집기가 기본으로 여는 루트 sub_type. fromV1 에 전용 분기가 있는 타입만 — 그 밖(file_URL2, materiality,
 * priority …)은 passthrough 라 편집할 게 없으니 구 편집기로 보낸다. 분기를 늘리면 여기도 같이 늘린다.
 * dropdown 루트는 변환은 되지만 실데이터에 없어 목록에서 뺀다.
 */
export const SUPPORTED_SUB_TYPES = ['radio', 'checkbox', 'table', 'guide_text', 'guide_text2', 'input_text', 'input_number', 'textbox']

let _seq = 0
export const newId = (prefix = 'n') => `${prefix}${++_seq}_${Math.random().toString(36).slice(2, 8)}`

export const makeNode = (type, props = {}) => ({
    id: props.id || newId(),
    type,
    label: props.label || '',
    ...props,
    children: props.children || []
})

/** 트리 전체를 훑는다. fn(node, depth, path, parent) */
export const walk = (node, fn, depth = 0, path = [], parent = null) => {
    if (!node) return
    fn(node, depth, path, parent)
    ;(node.columns || []).forEach((c, i) => walk(c, fn, depth + 1, path.concat([`열${i + 1}`]), node))
    ;(node.children || []).forEach((k, i) => walk(k, fn, depth + 1, path.concat([i + 1]), node))
}

export const findById = (root, id) => {
    let hit = null
    walk(root, (n, d, p, parent) => { if (n.id === id) hit = {node: n, depth: d, path: p, parent} })
    return hit
}

export const countNodes = (root) => { let c = 0; walk(root, () => { c++ }); return c }

export const maxDepth = (root) => { let d = 0; walk(root, (n, depth) => { if (depth > d) d = depth }); return d + 1 }

const clone = (o) => (o == null ? o : JSON.parse(JSON.stringify(o)))

/* ══════════════════════════════════════════════════
 * v1 → 트리
 * ══════════════════════════════════════════════════ */

/**
 * 행 객체들에서 컬럼 이름을 뽑는다.
 * table_header 가 있으면 그것을 쓰고, 없으면 행의 키에서 설정 키를 걷어내 추론한다.
 * 추론 시 *모든 행에 있는 키만* 컬럼으로 본다 — 옛 헤더 잔재는 그때 만들어진 행에만 남는다.
 * (행이 하나뿐이면 구분할 근거가 없어 전부 컬럼으로 본다)
 */
export const columnsFromRows = (rows, declaredHeader) => {
    if (Array.isArray(declaredHeader) && declaredHeader.length) return declaredHeader.slice()

    const valid = (rows || []).filter((r) => r && typeof r === 'object')
    if (!valid.length) return []

    const order = []
    const hits = new Map()
    valid.forEach((row) => {
        Object.keys(row).forEach((k) => {
            if (isMetaKey(k)) return
            if (!hits.has(k)) { hits.set(k, 0); order.push(k) }
            hits.set(k, hits.get(k) + 1)
        })
    })
    return order.filter((k) => hits.get(k) === valid.length)
}

/** 사이드카 번역 키를 노드의 i18n 필드로 걷어온다. */
const pickI18n = (src, baseKey) => {
    const out = {}
    Object.keys(LOCALE_SUFFIX).forEach((loc) => {
        const k = baseKey + LOCALE_SUFFIX[loc]
        if (src && src[k] !== undefined) out[loc] = src[k]
    })
    return Object.keys(out).length ? out : undefined
}

/**
 * 원본 행에서 그 컬럼이 갖고 있던 역할. cellContentFor 와 같은 판정을 쓴다.
 * toV1 이 "사용자가 이 칸을 바꿨는가" 를 알기 위해 필요하다 —
 * 노드에 새겨두면 노드를 통째로 교체했을 때 사라진다.
 */
export const roleFromSrcRow = (srcRow, colName) => {
    if (!srcRow || colName === undefined) return undefined
    const dd = srcRow.dropdown || {}
    const cc = srcRow.calculation || {}
    const ne = srcRow.noteditables || []
    if (Array.isArray(dd[colName]) && dd[colName].length) return 'dropdown'
    if (cc[colName]) return 'calc'
    if (ne.includes(colName)) return 'plain'
    return 'input'
}

/** 행 안에서 이 컬럼이 어떤 입력인지 판정해 칸 내용 노드를 만든다. */
const cellContentFor = (row, colName) => {
    const dropdown = row.dropdown || {}
    const calculation = row.calculation || {}
    const noteditables = row.noteditables || []
    const v1Placeholder = row.placeholder || {}
    const inputType = row.input_type || {}

    if (Array.isArray(dropdown[colName]) && dropdown[colName].length) {
        return makeNode('dropdown', {
            srcRole: 'dropdown',
            // 행에 값이 미리 박혀 있으면 응답자 화면에 그게 선택된 채로 뜬다.
            // 편집기에서 그대로 보여주기 위해 읽어만 둔다 (toV1 은 원본 클론이라 알아서 보존).
            srcValue: row[colName],
            value: row[colName] == null ? '' : String(row[colName]),
            // 손을 안 댔는지 판정해 원본을 그대로 되돌리기 위한 사본
            srcChoices: dropdown[colName].slice(),
            srcI18n: pickI18n(dropdown, colName),
            children: dropdown[colName].map((opt, i) => makeNode('plain_text', {
                label: String(opt),
                i18n: pickI18nAt(dropdown, colName, i)
            }))
        })
    }
    if (calculation[colName]) {
        return makeNode('calc', {srcRole: 'calc', formula: String(calculation[colName])})
    }
    if (noteditables.includes(colName)) {
        // 표시용 칸. 값과 그 번역이 라벨이 된다.
        return makeNode('plain_text', {
            srcRole: 'plain',
            srcValue: row[colName],           // null 과 '' 를 구분해 되돌리기 위해
            label: row[colName] == null ? '' : String(row[colName]),
            i18n: pickI18n(row, colName)
        })
    }
    const t = inputType[colName]
    return makeNode(t === 'number' ? 'input_number' : 'input_text', {
        srcRole: 'input',
        // 응답자 화면에 미리 채워져 뜨는 값. null 과 '' 를 구분해 되돌리기 위해 원본도 같이 든다.
        srcValue: row[colName],
        value: row[colName] == null ? '' : String(row[colName]),
        // v1 의 input_type 은 'text' | 'number' | 'date' 세 가지다. date 를 잃지 않도록 들고 다닌다.
        srcInputType: t,
        placeholder: v1Placeholder[colName] == null ? '' : String(v1Placeholder[colName]),
        placeholderI18n: pickI18n(v1Placeholder, colName)
    })
}

const tableNodeFrom = (contents, label) => {
    const rows = Array.isArray(contents.structure) ? contents.structure : []
    const colNames = columnsFromRows(rows, contents.table_header)

    // 열 노드는 원래 이름을 기억한다. 이름을 바꿔도 toV1 이 데이터를 새 키로 옮겨준다.
    const headerI18n = contents.table_header_ea || contents.table_header_ca
    const columns = colNames.map((name, i) => makeNode('column', {
        label: name,
        srcName: name,
        i18n: headerI18nFor(contents, i, name)
    }))

    const rowNodes = rows.map((row, i) => {
        const node = makeNode('row', {
            label: `${i + 1}행`,
            children: colNames.map((name) => {
                const content = cellContentFor(row || {}, name)
                // 필수 셀은 행의 required_cells 에 '열 이름' 으로 저장된다 (v1 EditingDFTable:630)
                if (content && ((row || {}).required_cells || []).indexOf(name) >= 0) content.required = true
                return makeNode('cell', {children: [content]})
            })
        })
        node._src = clone(row)      // 모델링하지 않은 키를 살리기 위한 원본
        return node
    })

    const node = makeNode('table', {label: label || contents.table_title || '', children: rowNodes})
    node.columns = columns
    node._src = clone(contents)
    if (headerI18n) node._hasHeaderI18n = true

    const orphans = []
    rows.forEach((row) => {
        if (!row || typeof row !== 'object') return
        Object.keys(row).forEach((k) => {
            if (!isMetaKey(k) && !colNames.includes(k) && !orphans.includes(k)) orphans.push(k)
        })
    })
    if (orphans.length) node.orphanKeys = orphans

    return node
}

/** table_header_ea/_ca 는 [{key,label}] 오버레이다. i 번째 열의 번역을 뽑는다. */
const headerI18nFor = (contents, idx, name) => {
    const out = {}
    Object.keys(LOCALE_SUFFIX).forEach((loc) => {
        const overlay = contents['table_header' + LOCALE_SUFFIX[loc]]
        if (!Array.isArray(overlay)) return
        const hit = overlay[idx]
        if (hit && typeof hit === 'object' && hit.key === name) out[loc] = hit.label
    })
    return Object.keys(out).length ? out : undefined
}

const elementNodeFrom = (el) => {
    if (!el || typeof el !== 'object') return null
    const type = el.type
    let node
    if (type === 'table') {
        node = tableNodeFrom(el, el.label || '')
        // 선택지 안 표에만 설명이 그려진다 (DFElement). 루트 표는 렌더러가 읽지 않는다.
        if (el.table_description !== undefined) node.description = el.table_description
        node.i18n = pickI18n(el, 'table_title')
        return node
    }
    if (type === 'input_text' || type === 'input_number') {
        const s = el.structure || {}
        node = makeNode(type, {
            label: s.pre_text || '',
            // 입력폼 제목 — DFElement 가 입력칸 위에 그린다 (pre_text 와 다른 자리다)
            formTitle: s.label || '',
            formTitleI18n: pickI18n(s, 'label'),
            placeholder: s.placeholder || '',
            afterText: s.after_text || '',
            // 번역 사이드카 — 백엔드 번역기(_translate.py)가 쓰는 키 그대로: pre_text_ea / placeholder_ea / after_text_ea
            i18n: pickI18n(s, 'pre_text'),
            placeholderI18n: pickI18n(s, 'placeholder'),
            afterTextI18n: pickI18n(s, 'after_text')
        })
    } else if (CHOICE_PARENTS.includes(type)) {
        node = makeNode(type, {
            label: el.label || '',
            children: (el.structure || []).map(choiceNodeFrom).filter(Boolean)
        })
    } else {
        node = makeNode(type || 'plain_text', {label: el.label || ''})
    }
    node._src = clone(el)
    return node
}

/** v1 의 선택지 항목 하나를 노드로. element_contents 는 자식이 된다. */
const choiceNodeFrom = (item) => {
    if (item == null) return null
    if (typeof item === 'string') return makeNode('plain_text', {label: item, srcWasString: true})

    // value 는 '저장 키' 다. 응답자가 고르면 이 값이 result 에 들어가고, 채점식·첨부·
    // 보완검토가 전부 이 값으로 답변을 되찾는다. text 와 같아 보여도 마찬가지다.
    // 그래서 원본에 value 가 있으면 언제나 들고 다닌다 — 문구를 다듬어도 키는 안 바뀌게.
    // (같을 때만 안 실으면, 문구 수정이 곧 키 변경이 되어 기존 답변이 통째로 미매칭된다)
    const node = makeNode('plain_text', {
        label: item.text == null ? '' : String(item.text),
        i18n: pickI18n(item, 'text'),
        ...(item.value != null ? {value: String(item.value)} : {})
    })
    // 선택지별 배점 (문항 설정의 채점 방식이 '선택지별 배점' 일 때 응답자 화면이 쓴다)
    if (item.score !== undefined) node.score = item.score
    // 상세 입력 — description 키의 '있음' 자체가 입력칸을 켜는 스위치다 (값은 응답자가 채운다).
    node.detail = item.description !== undefined
    if (item.placeholder !== undefined) node.detailPlaceholder = item.placeholder
    if (item.description_input_type !== undefined) node.detailType = item.description_input_type
    node.detailPlaceholderI18n = pickI18n(item, 'placeholder')
    const children = (item.element_contents || []).map(elementNodeFrom).filter(Boolean)
    if (children.length) node.children = children
    node._src = clone(item)
    return node
}

/**
 * 기존 문항(sub) 을 편집용 트리로 변환한다.
 * @returns {{root:object, notes:string[]}}
 */
export const fromV1 = (sub) => {
    if (!sub) throw new TypeError('fromV1: sub 가 비어 있습니다')

    const contents = typeof sub.sub_data_contents === 'string'
        ? JSON.parse(sub.sub_data_contents)
        : (sub.sub_data_contents || {})
    const type = sub.sub_type || 'plain_text'
    const notes = []

    let root
    if (type === 'table') {
        root = tableNodeFrom(contents, sub.sub_name || '')
    } else if (CHOICE_PARENTS.includes(type)) {
        root = makeNode(type, {
            label: sub.sub_name || '',
            children: (contents.structure || []).map(choiceNodeFrom).filter(Boolean)
        })
        root._src = clone(contents)
    } else if (type === 'guide_text' || type === 'guide_text2') {
        const body = Array.isArray(contents.structure) ? contents.structure : []
        const first = body[0]
        const gi = {}
        // guide_text2 는 배열 자리가 곧 번역이다 (GUIDE_SLOT). 사이드카보다 이쪽이 우선.
        if (type === 'guide_text2') {
            Object.keys(GUIDE_SLOT).forEach((loc) => {
                const v = body[GUIDE_SLOT[loc]]
                if (typeof v === 'string') gi[loc] = v
            })
        }
        // structure_ea / structure_ca 는 [문자열] 배열이고 백엔드 번역기는 [0] 만 쓴다
        Object.keys(LOCALE_SUFFIX).forEach((loc) => {
            if (gi[loc] !== undefined) return
            const arr = contents['structure' + LOCALE_SUFFIX[loc]]
            if (Array.isArray(arr) && arr[0] !== undefined) gi[loc] = arr[0]
        })
        // guide_text2 의 표시 옵션 — 있는 키만 담는다. 없던 키를 만들지 않기 위해 undefined 로 남긴다.
        let display, toggleTitle
        if (type === 'guide_text2') {
            const d = {}
            GUIDE2_DISPLAY_KEYS.forEach((k) => { if (contents[k] !== undefined) d[k] = contents[k] })
            if (Object.keys(d).length) display = d
            const tt = Array.isArray(contents.toggle_title) ? contents.toggle_title : null
            if (tt) {
                const t = {}
                if (typeof tt[0] === 'string') t.ko = tt[0]
                Object.keys(GUIDE_SLOT).forEach((loc) => {
                    const v = tt[GUIDE_SLOT[loc]]
                    if (typeof v === 'string') t[loc] = v
                })
                if (Object.keys(t).length) toggleTitle = t
            }
        }
        root = makeNode(type, {label: typeof first === 'string' ? first : '', i18n: Object.keys(gi).length ? gi : undefined,
            display, toggleTitle})
        root._src = clone(contents)
    } else if (type === 'input_text' || type === 'input_number') {
        // 루트 입력의 안내문은 **최상위** placeholder 다 (DFInputText.vue:63 / DFInputNumber.vue:64).
        // structure 는 prod 460건 전부 빈 배열이고 pre_text/after_text 를 가진 행은 0건이다 —
        // structure.* 는 선택지 안 요소(DFElement)용 계약이라 여기서 읽고 쓰면 아무도 안 보는 키가 된다.
        const s = (contents.structure && !Array.isArray(contents.structure)) ? contents.structure : {}
        root = makeNode(type, {
            label: sub.sub_name || '',
            placeholder: contents.placeholder == null ? (s.placeholder || '') : String(contents.placeholder),
            placeholderI18n: pickI18n(contents, 'placeholder') || pickI18n(s, 'placeholder')
        })
        root._src = clone(contents)
    } else if (type === 'textbox') {
        root = makeNode('textbox', {
            label: sub.sub_name || '',
            placeholder: contents.placeholder == null ? '' : String(contents.placeholder),
            placeholderI18n: pickI18n(contents, 'placeholder')
        })
        root._src = clone(contents)
    } else {
        root = makeNode(type, {label: sub.sub_name || ''})
        root._src = clone(contents)
        notes.push(`'${type}' 유형은 아직 전용 변환이 없어 저장 시 원본을 그대로 돌려보냅니다.`)
    }

    root.root = true
    root.subType = type

    walk(root, (n) => {
        if (n.orphanKeys && n.orphanKeys.length) {
            notes.push(`표에 현재 열에 없는 값이 남아 있습니다: ${n.orphanKeys.join(', ')}`)
        }
    })
    const d = maxDepth(root)
    if (d > 3) notes.push(`깊이 ${d}단계 — 기존 렌더러는 3단계까지만 다룹니다.`)

    return {root, notes}
}

/* ══════════════════════════════════════════════════
 * 트리 → v1  (계약 유지: 나가는 모양은 v1 그대로)
 * ══════════════════════════════════════════════════ */

/** 번역 사이드카 키를 원본 객체에 다시 심는다. 없으면 지운다. */
/**
 * 평행 배열 사이드카에서 i 번째 번역만 뽑는다.
 * 선택지 번역은 dropdown: {직무: [...], 직무_ea: [...]} 처럼 '같은 순서' 로만 연결된다.
 * 키가 없으니 각 선택지 노드가 자기 번역을 들고 다녀야 추가·삭제·순서변경에서 안 어긋난다.
 */
/** 드롭다운 번역이 원본 평행 배열과 달라졌는가 (번역 탭에서 고친 경우) */
const typedDiffers = (content, loc, kids) => {
    const src = content.srcI18n && content.srcI18n[loc]
    if (!Array.isArray(src)) return true
    if (src.length !== kids.length) return true
    return kids.some((c, i) => ((c.i18n && c.i18n[loc] !== undefined) ? c.i18n[loc] : undefined) !== src[i])
}

const pickI18nAt = (src, baseKey, i) => {
    const out = {}
    Object.keys(LOCALE_SUFFIX).forEach((loc) => {
        const arr = src && src[baseKey + LOCALE_SUFFIX[loc]]
        if (Array.isArray(arr) && arr[i] !== undefined) out[loc] = arr[i]
    })
    return Object.keys(out).length ? out : undefined
}

const writeI18n = (target, baseKey, i18n) => {
    Object.keys(LOCALE_SUFFIX).forEach((loc) => {
        const k = baseKey + LOCALE_SUFFIX[loc]
        if (i18n && i18n[loc] !== undefined) target[k] = i18n[loc]
        else delete target[k]
    })
}

const tableToV1 = (node) => {
    const out = clone(node._src) || {}
    const columns = node.columns || []
    const names = columns.map((c) => c.label)

    // 없던 키를 새로 만들지 않는다. table_header 가 생기면 v1 렌더러의
    // `if (table_header)` 분기가 켜져 동작이 달라진다(ST4v2 크래시 경로).
    // 원래 있었거나, 사용자가 열을 실제로 건드렸을 때만 쓴다.
    const hadHeader = Array.isArray((node._src || {}).table_header)
    const columnsEdited = columns.some((c) => c.srcName === undefined || c.srcName !== c.label) ||
        columns.length !== (columnsFromRows((node._src || {}).structure, (node._src || {}).table_header) || []).length
    if (hadHeader || columnsEdited) out.table_header = names

    // 헤더 번역 오버레이의 key 도 새 이름으로 맞춘다. 이게 있어야 저장 차단 가드에 안 걸린다.
    // 열이 그대로고 번역도 안 건드렸으면 손대지 않는다 — 원본 오버레이의 부가 키를 지우지 않기 위해.
    // 번역 탭에서 값을 넣었으면 열이 그대로여도 다시 쓰고, 없던 언어 오버레이도 그때 만든다.
    Object.keys(LOCALE_SUFFIX).forEach((loc) => {
        const key = 'table_header' + LOCALE_SUFFIX[loc]
        const hadOverlay = Array.isArray(out[key])
        const typed = columns.some((c) => c.i18n && c.i18n[loc] !== undefined)
        if (!(hadOverlay && columnsEdited) && !typed) return
        if (!hadOverlay && !typed) return
        const prev = hadOverlay ? out[key] : []
        out[key] = columns.map((c, i) => ({
            ...(prev[i] && typeof prev[i] === 'object' ? prev[i] : {}),
            key: c.label,
            label: (c.i18n && c.i18n[loc] !== undefined)
                ? c.i18n[loc]
                : (prev[i] && prev[i].label) || ''
        }))
    })

    out.structure = (node.children || []).map((rowNode) => {
        const r = clone(rowNode._src) || {}
        const src = rowNode._src || {}       // 원본 그대로. 역할 비교용

        // 열 이름이 바뀌었으면 값과 번역을 새 키로 옮긴다 (지금은 그냥 끊긴다).
        columns.forEach((col) => {
            const from = col.srcName
            const to = col.label
            if (!from || from === to) return
            if (Object.prototype.hasOwnProperty.call(r, from)) { r[to] = r[from]; delete r[from] }
            Object.keys(LOCALE_SUFFIX).forEach((loc) => {
                const sfx = LOCALE_SUFFIX[loc]
                if (Object.prototype.hasOwnProperty.call(r, from + sfx)) {
                    r[to + sfx] = r[from + sfx]; delete r[from + sfx]
                }
            })
        })

        // 맵을 새로 만들지 않고 원본에서 출발한다. 맵 안에도 번역 사이드카가 들어 있다
        // (예: dropdown: {직무: [...], 직무_ea: [...]}). 새로 만들면 그게 날아간다.
        const dropdown = clone(r.dropdown) || {}
        const calculation = clone(r.calculation) || {}
        const inputType = clone(r.input_type) || {}
        const placeholder = clone(r.placeholder) || {}
        const srcNoteditables = Array.isArray(r.noteditables) ? r.noteditables.slice() : []
        const managedNoteditables = []

        // 맵 '안' 의 키는 번역 사이드카를 달고 있다(dropdown: {직무, 직무_ea}).
        // 한쪽만 지우면 고아 번역이 남아 응답자 화면이 언어마다 갈린다 —
        // 한국어에선 자유 입력, 영문에선 드롭다운이 뜨고 클릭하면 죽는다.
        const dropWithSidecars = (map, key) => {
            if (key === undefined) return
            delete map[key]
            Object.keys(LOCALE_SUFFIX).forEach((loc) => { delete map[key + LOCALE_SUFFIX[loc]] })
        }
        // 열 이름이 바뀐 경우에만 옛 키를 정리한다(이름이 그대로면 지울 게 없다).
        const dropStaleKeys = (map, from, to) => {
            if (from === to) return
            dropWithSidecars(map, from)
        }

        columns.forEach((col, i) => {
            const name = col.label
            const cell = (rowNode.children || [])[i]
            const content = cell && cell.children && cell.children[0]
            if (!content) return

            const roleNow = content.type === 'plain_text' ? 'plain'
                : content.type === 'dropdown' ? 'dropdown'
                : content.type === 'calc' ? 'calc' : 'input'
            // 새로 만든 열은 지울 옛 흔적이 없다.
            const roleWas = col.srcName === undefined ? undefined : roleFromSrcRow(src, col.srcName)
            const roleChanged = roleWas !== undefined && roleWas !== roleNow

            // 역할이 바뀐 칸만 옛 역할의 흔적을 지운다.
            // 원본에 모순이 있어도(같은 컬럼이 noteditables 이면서 input_type 인 경우가 실제로 있다)
            // 사용자가 안 건드렸으면 그대로 둔다 — 계약 유지가 목적이지 정리가 목적이 아니다.
            if (roleChanged) {
                // 이름이 함께 바뀌었을 수 있으니 옛 이름 기준으로 지운다.
                const was = col.srcName === undefined ? name : col.srcName
                // 역할이 바뀌면 이름이 그대로여도 사이드카까지 지운다.
                if (roleNow !== 'dropdown') dropWithSidecars(dropdown, was)
                if (roleNow !== 'calc') delete calculation[was]
                if (roleNow !== 'input') delete inputType[was]
            }

            if (content.type === 'plain_text') {
                managedNoteditables.push(name)
                // 안 건드렸으면 원본 그대로 (null 을 '' 로 바꾸지 않는다)
                const unchanged = Object.prototype.hasOwnProperty.call(content, 'srcValue') &&
                    content.label === (content.srcValue == null ? '' : String(content.srcValue))
                r[name] = unchanged ? content.srcValue : content.label
                writeI18n(r, name, content.i18n)
            } else if (content.type === 'dropdown') {
                const kids = content.children || []
                const labels = kids.map((c) => c.label)
                // 손 안 댔으면 원본을 그대로 되돌린다 (길이가 어긋난 기존 데이터를 임의로 고치지 않는다)
                const untouched = Array.isArray(content.srcChoices) &&
                    content.srcChoices.length === labels.length &&
                    content.srcChoices.every((v, k) => String(v) === labels[k])
                dropdown[name] = untouched ? content.srcChoices : labels
                Object.keys(LOCALE_SUFFIX).forEach((loc) => {
                    const key = name + LOCALE_SUFFIX[loc]
                    // 원본에 없던 번역을 새로 만들지 않는다 — 없던 사이드카가 생기면
                    // DFTable 의 로케일 분기가 켜져 동작이 달라진다.
                    const typed = kids.some((c) => c.i18n && c.i18n[loc] !== undefined)
                    if ((!content.srcI18n || content.srcI18n[loc] === undefined) && !typed) { delete dropdown[key]; return }
                    dropdown[key] = (untouched && content.srcI18n && content.srcI18n[loc] !== undefined && !typedDiffers(content, loc, kids))
                        ? content.srcI18n[loc]
                        : kids.map((c) => (c.i18n && c.i18n[loc] !== undefined ? c.i18n[loc] : ''))
                })
                dropStaleKeys(dropdown, col.srcName, name)
                writeCellValue(r, name, content)
            } else if (content.type === 'calc') {
                calculation[name] = content.formula || ''
            } else {
                // v1 은 'text' 를 생략하고 없으면 text 로 본다. 없던 걸 명시로 바꾸지 않는다.
                if (content.type === 'input_number') inputType[name] = 'number'
                else if (content.srcInputType === 'date') inputType[name] = 'date'   // 날짜 칸을 단답으로 뭉개지 않는다
                else if (inputType[name] !== undefined && inputType[name] !== null) inputType[name] = 'text'
                if (content.placeholder) placeholder[name] = content.placeholder
                else if (roleChanged) delete placeholder[name]
                writeI18n(placeholder, name, content.placeholderI18n)
                writeCellValue(r, name, content)
            }
        })

        // noteditables: 원본을 그대로 두고 이름 변경만 반영한다.
        // 역할이 바뀐 칸만 추가/제거한다 — 기존 중복 항목·빈 문자열은 임의로 정리하지 않는다.
        const noteditables = srcNoteditables.map((x) => {
            const col = columns.find((c) => c.srcName === x)
            return col ? col.label : x
        })
        columns.forEach((col, i) => {
            const cell = (rowNode.children || [])[i]
            const content = cell && cell.children && cell.children[0]
            if (!content || col.srcName === undefined) return
            const nowPlain = content.type === 'plain_text'
            const wasPlain = roleFromSrcRow(src, col.srcName) === 'plain'
            if (nowPlain && !wasPlain && !noteditables.includes(col.label)) noteditables.push(col.label)
            if (!nowPlain && wasPlain) {
                const at = noteditables.indexOf(col.label)
                if (at >= 0) noteditables.splice(at, 1)
            }
        })
        // 원본에 없던 칸(새로 만든 열)이 표시용이면 추가한다
        managedNoteditables.forEach((n) => {
            const col = columns.find((c) => c.label === n)
            if (col && col.srcName === undefined && !noteditables.includes(n)) noteditables.push(n)
        })

        // 원래 있던 키만 다시 쓴다. 비어 있고 원본에도 없던 키는 만들지 않는다 —
        // 계약 유지가 목적이므로 payload 를 임의로 살찌우지 않는다.
        const put = (key, val, isEmpty) => {
            if (!isEmpty || Object.prototype.hasOwnProperty.call(src, key)) r[key] = val
            else delete r[key]
        }
        put('dropdown', dropdown, !Object.keys(dropdown).length)
        put('calculation', calculation, !Object.keys(calculation).length)
        put('input_type', inputType, !Object.keys(inputType).length)
        put('noteditables', noteditables, !noteditables.length)
        put('placeholder', placeholder, !Object.keys(placeholder).length)

        // 필수 셀 — 지금의 열 이름으로 다시 쓴다. 이름을 바꾸거나 열을 지워도 지정이 어긋나지 않는다.
        const requiredCells = []
        columns.forEach((col, i) => {
            const cell = (rowNode.children || [])[i]
            const content = cell && cell.children && cell.children[0]
            if (content && content.required && REQUIRABLE_CELL.indexOf(content.type) >= 0) requiredCells.push(col.label)
        })
        put('required_cells', requiredCells, !requiredCells.length)
        return r
    })

    return out
}

/** 입력 계열의 structure. 원래 없던 키는 값이 있을 때만 만든다. */
/**
 * 칸에 미리 채워지는 값(row[열]). plain_text 와 같은 규칙 — 안 건드렸으면 원본을 그대로 둔다.
 * null 을 '' 로 바꾸면 응답자 화면에서 빈 칸과 미입력이 구분되지 않는다.
 */
const writeCellValue = (r, name, content) => {
    if (content.value === undefined) return
    const unchanged = Object.prototype.hasOwnProperty.call(content, 'srcValue') &&
        content.value === (content.srcValue == null ? '' : String(content.srcValue))
    r[name] = unchanged ? content.srcValue : content.value
}

const writeInputStructure = (srcStructure, node) => {
    const out = {...(srcStructure || {})}
    const put = (key, val) => {
        if (val) out[key] = val
        else if (Object.prototype.hasOwnProperty.call(out, key)) out[key] = val || ''
    }
    put('pre_text', node.label || '')
    put('after_text', node.afterText || '')
    put('placeholder', node.placeholder || '')
    if (node.formTitle !== undefined || out.label !== undefined) put('label', node.formTitle || '')
    if (node.formTitleI18n !== undefined ||
        Object.keys(LOCALE_SUFFIX).some((l) => Object.prototype.hasOwnProperty.call(out, 'label' + LOCALE_SUFFIX[l]))) {
        writeI18n(out, 'label', node.formTitleI18n)
    }
    // 번역 사이드카. 루트 입력은 label 이 sub_name 이라 pre_text 번역이 없다 — 있을 때만 쓴다.
    if (node.i18n) writeI18n(out, 'pre_text', node.i18n)
    if (node.afterTextI18n !== undefined || Object.keys(LOCALE_SUFFIX).some((l) => Object.prototype.hasOwnProperty.call(out, 'after_text' + LOCALE_SUFFIX[l]))) writeI18n(out, 'after_text', node.afterTextI18n)
    if (node.placeholderI18n !== undefined || Object.keys(LOCALE_SUFFIX).some((l) => Object.prototype.hasOwnProperty.call(out, 'placeholder' + LOCALE_SUFFIX[l]))) writeI18n(out, 'placeholder', node.placeholderI18n)
    return out
}

const nodeToElement = (node) => {
    const base = clone(node._src) || {type: node.type}
    if (node.type === 'table') {
        const t = {...base, ...tableToV1(node), type: 'table'}
        // 중첩 표의 label 은 table_title 이다 (elementNodeFrom → tableNodeFrom 이 그렇게 읽는다).
        // 루트 표의 label 은 sub_name 이라 tableToV1 은 label 을 쓰지 않는다 — 여기서만 쓴다.
        if (node.label || Object.prototype.hasOwnProperty.call(base, 'table_title')) t.table_title = node.label || ''
        if (node.description !== undefined || Object.prototype.hasOwnProperty.call(base, 'table_description')) {
            t.table_description = node.description || ''
        }
        if (node.i18n !== undefined ||
            Object.keys(LOCALE_SUFFIX).some((l) => Object.prototype.hasOwnProperty.call(base, 'table_title' + LOCALE_SUFFIX[l]))) {
            writeI18n(t, 'table_title', node.i18n)
        }
        return t
    }
    if (node.type === 'input_text' || node.type === 'input_number') {
        base.type = node.type
        base.structure = writeInputStructure(base.structure, node)
        return base
    }
    if (CHOICE_PARENTS.includes(node.type)) {
        base.type = node.type
        base.structure = (node.children || []).map(choiceToV1)
        return base
    }
    base.type = node.type
    return base
}

const choiceToV1 = (node) => {
    // 선택지 자리에 블록 노드가 있으면 감싼다. v1 의 선택지는 {text, value} 객체이고
    // 블록은 element_contents 안에만 들어갈 수 있다.
    if (nodeMeta(node.type).fit === 'block' && !CHOICE_PARENTS.includes(node.type)) {
        return {
            text: node.label || '',
            value: node.label || '',
            element_contents: [nodeToElement(node)]
        }
    }
    // 원본이 문자열이었으면 문자열로 되돌린다 (객체로 바꾸면 렌더러 계약이 달라진다)
    if (node.srcWasString && !(node.children && node.children.length)) return node.label
    const out = clone(node._src) || {}
    out.text = node.label
    out.value = node.value !== undefined ? node.value : node.label
    writeI18n(out, 'text', node.i18n)

    if (node.score !== undefined && node.score !== '') out.score = node.score
    else if (node.score === '' ) delete out.score
    // 상세 입력 스위치. 켜면 키를 만들고(응답자가 채울 자리), 끄면 관련 키를 함께 지운다.
    if (node.detail) {
        if (out.description === undefined) out.description = ''
        if (node.detailPlaceholder !== undefined) out.placeholder = node.detailPlaceholder
        if (node.detailType !== undefined) out.description_input_type = node.detailType
        writeI18n(out, 'placeholder', node.detailPlaceholderI18n)
    } else if (node.detail === false) {
        delete out.description
        delete out.placeholder
        delete out.description_input_type
        Object.keys(LOCALE_SUFFIX).forEach((loc) => { delete out['placeholder' + LOCALE_SUFFIX[loc]] })
    }
    if (node.children && node.children.length) out.element_contents = node.children.map(nodeToElement)
    else if (Array.isArray(out.element_contents)) out.element_contents = []   // 있던 키는 없애지 않는다
    return out
}

/**
 * 편집 트리를 v1 sub_data_contents 로 되돌린다.
 * 원본(_src)을 복제한 뒤 편집기가 소유한 부분만 덮어쓰므로,
 * 모델링하지 않은 키는 그대로 살아남는다.
 */
export const toV1 = (root) => {
    if (!root) throw new TypeError('toV1: root 가 비어 있습니다')
    const type = root.subType || root.type

    if (type === 'table') return tableToV1(root)

    const out = clone(root._src) || {}
    if (CHOICE_PARENTS.includes(type)) {
        out.structure = (root.children || []).map(choiceToV1)
        return out
    }
    if (type === 'guide_text2') {
        // 배열 자리를 지우지 않는다. 고치지 않은 언어는 원본을 그대로 둔다.
        const next = Array.isArray(out.structure) ? out.structure.slice() : []
        next[0] = root.label
        Object.keys(GUIDE_SLOT).forEach((loc) => {
            const at = GUIDE_SLOT[loc]
            const v = root.i18n && root.i18n[loc]
            if (v === undefined) return
            for (let k = next.length; k < at; k++) next[k] = ''   // 구멍을 null 로 남기지 않는다
            next[at] = v
        })
        out.structure = next

        // 표시 옵션 — 편집기가 건드린 키만 쓴다. 비우면 키를 빼서 렌더러 기본값으로 돌린다.
        GUIDE2_DISPLAY_KEYS.forEach((k) => {
            const v = root.display ? root.display[k] : undefined
            if (v === undefined) return
            if (v === '') { delete out[k]; return }
            out[k] = v
        })
        // 접기 버튼 제목도 자리 배열이다 (0=국문 / GUIDE_SLOT). 건드린 적 없으면 그대로 둔다.
        const tt = root.toggleTitle
        if (tt && Object.keys(tt).some((k) => tt[k] !== undefined)) {
            const arr = Array.isArray(out.toggle_title) ? out.toggle_title.slice() : []
            const put = (at, v) => {
                if (v === undefined) return
                for (let k = arr.length; k < at; k++) arr[k] = ''
                arr[at] = v
            }
            put(0, tt.ko)
            Object.keys(GUIDE_SLOT).forEach((loc) => put(GUIDE_SLOT[loc], tt[loc]))
            out.toggle_title = arr
        }

        // 사이드카는 원래 있던 것만 갱신한다 (prod 에는 0건 — 없던 키를 만들지 않는다)
        Object.keys(LOCALE_SUFFIX).forEach((loc) => {
            const k = 'structure' + LOCALE_SUFFIX[loc]
            if (!Object.prototype.hasOwnProperty.call(out, k)) return
            const v = root.i18n && root.i18n[loc]
            if (v !== undefined) out[k] = [v]
        })
        return out
    }
    if (type === 'guide_text') {
        out.structure = [root.label]
        // 번역은 [문자열] 배열로. 원본에 없던 사이드카를 만들지는 않는다 — 편집기에서 값을 넣었을 때만.
        Object.keys(LOCALE_SUFFIX).forEach((loc) => {
            const k = 'structure' + LOCALE_SUFFIX[loc]
            const v = root.i18n && root.i18n[loc]
            if (v !== undefined && v !== '') out[k] = [v]
            else if (Object.prototype.hasOwnProperty.call(out, k)) out[k] = v === '' ? [''] : out[k]
        })
        return out
    }
    if (type === 'input_text' || type === 'input_number') {
        // 최상위 placeholder 만 쓴다. structure 는 원본(_src) 그대로 둔다 — 빈 배열을 객체로
        // 바꿔 쓰면 렌더러가 읽지도 않는 키가 생기고 계약이 흔들린다.
        const same = root.placeholder === (out.placeholder == null ? '' : String(out.placeholder))
        if (!same) out.placeholder = root.placeholder || ''
        if (root.placeholderI18n !== undefined ||
            Object.keys(LOCALE_SUFFIX).some((l) => Object.prototype.hasOwnProperty.call(out, 'placeholder' + LOCALE_SUFFIX[l]))) {
            writeI18n(out, 'placeholder', root.placeholderI18n)
        }
        return out
    }
    if (type === 'textbox') {
        const same = root.placeholder === (out.placeholder == null ? '' : String(out.placeholder))
        if (!same) out.placeholder = root.placeholder || ''
        if (root.placeholderI18n !== undefined || Object.keys(LOCALE_SUFFIX).some((l) => Object.prototype.hasOwnProperty.call(out, 'placeholder' + LOCALE_SUFFIX[l]))) writeI18n(out, 'placeholder', root.placeholderI18n)
        return out
    }
    return out   // 전용 변환이 없는 유형은 원본 그대로
}

/* ══════════════════════════════════════════════════
 * 표 조작 — 컴포넌트에 묻지 않고 여기서 한다 (테스트 가능하게)
 * ══════════════════════════════════════════════════ */

/** 칸 하나. 셀은 노드를 담는 자리일 뿐이고 동작은 안에 든 것이 정한다. */
export const makeCell = (content) => makeNode('cell', {
    children: [content || makeNode('input_text', {placeholder: ''})]
})

/**
 * 빈 표 골격. 유형을 table 로 바꿀 때 이걸로 초기화한다.
 * 열 이름 기본값에 연도를 박지 않는다 — 매년 손대야 하고, 헤더를 고치면
 * 번역 정합성 가드에 걸린다.
 */
export const makeTable = ({columnNames = ['구분'], rowCount = 1, label = ''} = {}) => {
    const node = makeNode('table', {label})
    node.columns = columnNames.map((n) => makeNode('column', {label: n}))
    node.children = Array.from({length: rowCount}, (_, i) => makeNode('row', {
        label: `${i + 1}행`,
        children: columnNames.map(() => makeCell())
    }))
    return node
}

/**
 * 행을 그대로 복제해 바로 아래에 넣는다. 열이 많고 칸 유형·드롭다운이 촘촘한 표에서
 * 빈 행을 추가하면 열 개수만큼 다시 설정해야 한다.
 * 새 id 를 주지 않으면 findById 가 원본을 집는다.
 */
export const duplicateRow = (table, index) => {
    const rows = table.children || []
    const src = rows[index]
    if (!src) return null
    const reid = (n) => {
        n.id = newId()
        ;(n.columns || []).forEach(reid)
        ;(n.children || []).forEach(reid)
        return n
    }
    const copy = reid(JSON.parse(JSON.stringify(src)))
    copy.label = `${index + 2}행`
    rows.splice(index + 1, 0, copy)
    rows.forEach((r, i) => { r.label = `${i + 1}행` })
    return copy
}

/** 표 노드를 제자리에서 표로 만든다 (유형 변경 시). 이미 열이 있으면 건드리지 않는다. */
export const ensureTableShape = (node) => {
    if (!node.columns || !node.columns.length) {
        const seed = makeTable()
        node.columns = seed.columns
        node.children = seed.children
    }
    return node
}

export const addColumn = (table, name = '새 열') => {
    const col = makeNode('column', {label: name})
    table.columns = (table.columns || []).concat([col])
    ;(table.children || []).forEach((row) => { row.children = (row.children || []).concat([makeCell()]) })
    return col
}

export const removeColumn = (table, index) => {
    if (!table.columns || index < 0 || index >= table.columns.length) return
    table.columns.splice(index, 1)
    ;(table.children || []).forEach((row) => { (row.children || []).splice(index, 1) })
}

export const addRow = (table) => {
    const row = makeNode('row', {
        label: `${(table.children || []).length + 1}행`,
        children: (table.columns || []).map(() => makeCell())
    })
    table.children = (table.children || []).concat([row])
    return row
}

export const removeRow = (table, index) => {
    if (!table.children || index < 0 || index >= table.children.length) return
    table.children.splice(index, 1)
    table.children.forEach((r, i) => { r.label = `${i + 1}행` })
}

/** 열 순서 바꾸기. 각 행의 칸도 같이 옮긴다. */
export const moveColumn = (table, from, to) => {
    const cols = table.columns || []
    if (from === to || from < 0 || to < 0 || from >= cols.length || to >= cols.length) return
    cols.splice(to, 0, cols.splice(from, 1)[0])
    ;(table.children || []).forEach((row) => {
        const cs = row.children || []
        cs.splice(to, 0, cs.splice(from, 1)[0])
    })
}


/* ══════════════════════════════════════════════════
 * 어떤 노드가 자식/형제를 받을 수 있는가
 * — 못 받는 자리에 넣으면 toV1 이 조용히 버린다. 화면에서 미리 막는다.
 * ══════════════════════════════════════════════════ */

/** 자식을 담을 수 있는가. 못 담으면 이유를 돌려준다. */
export const whyNoChild = (node, parent) => {
    if (!node) return '노드를 고르세요'
    if (node.type === 'cell') return '칸은 노드를 하나만 담습니다. 유형을 바꾸세요'
    if (node.type === 'table') return '표에는 행을 추가하세요'
    if (node.type === 'row') return '행의 칸은 열을 추가하면 생깁니다'
    if (node.type === 'column') return '열에는 자식을 붙일 수 없습니다'
    // 칸 안에 든 내용은 그 자체가 잎이다
    if (parent && parent.type === 'cell') return '칸 안의 내용에는 자식을 붙일 수 없습니다'
    if (nodeMeta(node.type).fit === 'inline' && !CHOICE_PARENTS.includes(node.type)) {
        return `'${nodeMeta(node.type).ko}' 은(는) 자식을 갖지 않습니다`
    }
    return ''
}

/** 형제를 받을 수 있는가. 못 받으면 이유를 돌려준다. */
export const whyNoSibling = (node, parent) => {
    if (!node) return '노드를 고르세요'
    if (!parent) return '루트에는 형제를 붙일 수 없습니다'
    if (node.type === 'cell') return '칸은 열을 추가해야 늘어납니다'
    if (node.type === 'column') return '열은 「+ 열」 로 추가하세요'
    if (node.type === 'row') return '행은 「+ 행」 으로 추가하세요'
    if (parent.type === 'cell') return '칸은 노드를 하나만 담습니다'
    return ''
}
